import React, { useCallback, useState } from "react";
import get from "lodash/get";
import {
  NOTIFICATION_STATUSES,
  useNotifications,
} from "@adsk/alloy-react-notification";
import type { Issue, IssueAttributes } from "@adsk/acc-issues-types";
import { IssueStatus } from "@adsk/acc-issues-types";
import {
  CreationModalWrapper,
  CreationModalContent,
  CreationModalProps,
} from "../utils/components/CreationModal";
import { sanitizeIssueAttributeMap } from "../utils/data/issueFieldSanitization";
import { useInitialType } from "./useInitialType";
import { IssueCreationForm } from "./IssueCreationForm";
import { useIssueValidationSchema } from "../utils/validation/entitySchemas";
import { IssueFormAttributes } from "@adsk/acc-issues-core/utils/types/Issue";
import { useDispatch } from "@adsk/acc-issues-core/state/redux/hooks";
import { useTranslations } from "@adsk/acc-issues-translations";
import { v4 as uuid } from "uuid";
import { MarkupCreationPayload } from "@adsk/acc-issues-types";
import { useLocalizedDecimalSeparator } from "@adsk/acc-issues-core/utils/hooks/useLocalizedDecimalSeparator";
import { TabTitle } from "@adsk/exo-skeleton-react";
import {
  CustomAttachmentDraft,
  getIssueWithAttachmentsRequestData,
} from "../utils/data/attachmentsHelpers";
import { createIssueWithAttachmentsAction } from "@adsk/acc-issues-core/state/redux/ducks/issueAttachments";
import { useCreateIssue } from "../hooks/useCreateIssue";

export type IssueCreationModalProps = CreationModalProps<Issue> & {
  fixedTypeId?: string;
  locationId?: string;
};

export const IssueCreationModalContent = ({
  onClose,
  onCreate,
  onDiscardConfirm,
  onDiscardAbort,
  analyticsData = {},
  fixedTypeId = undefined,
  locationId,
}: IssueCreationModalProps) => {
  const dispatch = useDispatch();
  const { T } = useTranslations();
  const { showNotification } = useNotifications();
  const [errors, setErrors] = useState({});
  const { sanitizeIssueCustomAttributes } = useLocalizedDecimalSeparator();
  const createIssue = useCreateIssue();

  const createEntity = useCallback(
    async (formValues: IssueFormAttributes) => {
      setErrors({});
      const issueId = uuid();

      try {
        const {
          templateId,
          placements,
          snapshotBlob,
          attachments,
          ...issueAttributes
        } = sanitizeIssueAttributeMap(formValues) as IssueAttributes & {
          placements?: MarkupCreationPayload;
          templateId?: string;
          snapshotBlob?: Blob;
          attachments?: CustomAttachmentDraft[];
        };

        const issueDataToCreate: IssueAttributes = {
          ...issueAttributes,
          placements,
        };

        const handleAttachmentCreationFailure = () => {
          setErrors({
            ...errors,
            attachments: T.translate("issueCreation.attachments.creationError"),
          });
          showNotification({
            status: NOTIFICATION_STATUSES.ERROR,
            message: T.translate("issueCreation.attachments.failureMessage"),
          });
        };

        // mutates the custom attribute objects to avoid having it in memory twice
        sanitizeIssueCustomAttributes(issueDataToCreate.customAttributes);

        const createIssueWithAttachmentsFunc = async (
          issueData: IssueAttributes
        ): Promise<Issue> => {
          //The endpoint of creating issue with attachment requires the issue id to be in the issueData object
          issueData.id = issueId;
          const issueWithAttachmentsToCreate =
            getIssueWithAttachmentsRequestData({
              domainEntity: issueData,
              attachments: attachments || [],
            });
          const result = await dispatch(
            createIssueWithAttachmentsAction(issueWithAttachmentsToCreate)
          );
          if (!result) {
            throw new Error("Failed to create issue with attachments");
          }
          return result;
        };

        const responseIssueData = await createIssue({
          snapshotBlob: Promise.resolve(snapshotBlob),
          customCreateFn: attachments?.length
            ? createIssueWithAttachmentsFunc
            : undefined,
          issueAttributes: issueDataToCreate,
          onFailure: (error: any) => {
            const isAttachmentError =
              error?.errorCode === "ISSUES_SERVICE_FAILED_TO_CREATE_ATTACHMENT";
            if (!isAttachmentError) {
              throw error;
            }
            handleAttachmentCreationFailure();
          },
          onSuccess: () => {
            showNotification({
              status: NOTIFICATION_STATUSES.SUCCESS,
              message: T.translate("issueCreation.successMessage"),
            });
          },
          analyticsData: {
            ...analyticsData,
            referencePicker: analyticsData.referencePicker ?? "none",
            origin: analyticsData.origin ?? "issue_log",
          },
        });
        return [responseIssueData];
      } catch {
        showNotification({
          status: NOTIFICATION_STATUSES.ERROR,
          message: T.translate("issueCreation.failureMessage"),
        });
      }
    },
    [
      sanitizeIssueCustomAttributes,
      createIssue,
      analyticsData,
      errors,
      showNotification,
      T,
      dispatch,
    ]
  );

  // This is the last selected type, (retrieved from local storage),
  // or, on first load: "General" . (or the first in the list if "General" doesn't exist)
  // or, the fixed type when fixedTypeId is supplied
  const initialType = useInitialType(fixedTypeId);

  const initialValues = {
    title: get(initialType, "title", ""),
    issueTypeId: fixedTypeId ?? get(initialType, "id", ""),
    status: IssueStatus.Open,
    published: true,
    customAttributes: {},
    ...(locationId ? { locationId } : {}),
  };

  const issueValidationSchema = useIssueValidationSchema({ fixedTypeId });

  return (
    <CreationModalContent
      onClose={onClose}
      onCreate={onCreate}
      title={T.translate("issueCreation.modalTitle")}
      errors={errors}
      initialValues={initialValues}
      createEntity={createEntity}
      validationSchema={issueValidationSchema}
      onDiscardConfirm={onDiscardConfirm}
      onDiscardAbort={onDiscardAbort}
      validateOnMount={!!fixedTypeId}
    >
      <TabTitle value={T.translate("issueCreation.modalTitle") as string} />
      <IssueCreationForm fixedTypeId={fixedTypeId} />
    </CreationModalContent>
  );
};

export const IssueCreationModal = ({
  isOpen = true,
  ...props
}: IssueCreationModalProps) => {
  return (
    <CreationModalWrapper isOpen={isOpen}>
      <IssueCreationModalContent {...props} />
    </CreationModalWrapper>
  );
};
